/* eslint-disable no-redeclare */
import { ADMIN_PERMISSIONS, ADMIN_ROLE, PROVIDER_PERMISSIONS } from "@tellescope/constants";
import { useMemo } from "react";
import { type TellescopeModelFromName } from "../../mappings";
import { type UseTellescopeInfiniteQueryResult } from "../types";
import { useTellescopeInfiniteQuery } from "../use-tellescope-infinite-query";
import { type OrgPlanName, useOrgPlan } from "./org-onboarding";

export type RoleRecord = TellescopeModelFromName<"role_based_access_permissions">;
export type RolePermissions = RoleRecord["permissions"];

/**
 * UI-only sentinel value for the implicit non-admin baseline. Team members without any custom role fall
 * back to {@link PROVIDER_PERMISSIONS}, but that baseline has no backend role string — it maps to an
 * empty/cleared role. This sentinel stands in for it as a selectable option value and is converted back
 * to `""` before any write. It is deliberately not the plain "Non-Admin" text so a custom role a user
 * happens to name "Non-Admin" can't collide with the baseline.
 */
export const NON_ADMIN_ROLE = "__tellescope_non_admin_baseline__";

/**
 * A built-in role that every org has but that is never persisted in `role_based_access_permissions`, so it
 * never comes back from a roles query. Consumers surface these alongside the org's stored custom roles.
 */
export interface DefaultRole {
  role: string;
  permissions: RolePermissions;
}

/**
 * The implicit "Admin" role. Users whose `roles` include {@link ADMIN_ROLE} get {@link ADMIN_PERMISSIONS}
 * (full access). It isn't stored, so it never appears in role queries.
 */
export const DEFAULT_ADMIN_ROLE: DefaultRole = { role: ADMIN_ROLE, permissions: ADMIN_PERMISSIONS };

/**
 * The implicit non-admin baseline applied to team members without a custom role. Its `role` is the
 * {@link NON_ADMIN_ROLE} sentinel rather than a real backend string.
 */
export const DEFAULT_NON_ADMIN_ROLE: DefaultRole = { role: NON_ADMIN_ROLE, permissions: PROVIDER_PERMISSIONS };

/** The built-in Admin and Non-Admin roles, in display order. */
export const DEFAULT_ROLES: DefaultRole[] = [DEFAULT_ADMIN_ROLE, DEFAULT_NON_ADMIN_ROLE];

/**
 * Either a stored custom role record or one of the built-in {@link DefaultRole}s. The merged `roles`
 * return is a list of these — note a {@link DefaultRole} only carries `role` + `permissions`, so consumers
 * that need full record fields (`id`, `color`, `description`, …) should read the raw `data` instead.
 */
export type RoleOrDefault = RoleRecord | DefaultRole;

/**
 * Plans that don't include role-based access permissions. Orgs on these plans only have the built-in
 * Admin / Non-Admin roles, so there are no stored records to fetch and the roles query is disabled.
 */
export const PLANS_WITHOUT_ROLE_BASED_ACCESS: readonly OrgPlanName[] = ["Healthie-Text"];

/** Whether the org's plan includes role-based access permissions. Unknown plans grant everything. */
export const planSupportsRoleBasedAccess = (plan: OrgPlanName | undefined) =>
  !plan || !PLANS_WITHOUT_ROLE_BASED_ACCESS.includes(plan);

/**
 * Returns the merged default + custom roles unchanged. Used as the default `select` so callers that
 * don't provide one still receive the plain {@link RoleOrDefault} array.
 */
const identityRolesSelect = (roles: RoleOrDefault[]): RoleOrDefault[] => roles;

export interface UseRolesQueryArgs<TSelected = RoleOrDefault[]> {
  /** Free-text search forwarded to the roles `getSome` request. */
  search?: string;
  /** Force-disable the query regardless of plan gating (e.g. while a dependency is still loading). */
  enabled?: boolean;
  /**
   * Optional transform applied to the merged default + custom roles before they're returned as `roles`.
   * Runs in a memo, so the result is only recomputed when the underlying data changes. Defaults to
   * returning the merged roles unchanged.
   */
  select?: (roles: RoleOrDefault[]) => TSelected;
}

/** The query result shared by both {@link useRolesQuery} overloads. `data` is the raw custom role records. */
type UseRolesQueryResult = UseTellescopeInfiniteQueryResult<
  "role_based_access_permissions",
  "getSome",
  Error,
  RoleRecord[]
> & {
  defaultRoles: DefaultRole[];
  hasRoleBasedAccess: boolean;
};

/**
 * The single source of truth for the org's roles: it fetches the custom role records from
 * `role_based_access_permissions` and merges in the built-in {@link DEFAULT_ROLES} (Admin / Non-Admin)
 * that every org has but that are never persisted.
 *
 * The underlying query is automatically disabled when the org's plan doesn't include role-based access
 * permissions (see {@link planSupportsRoleBasedAccess}) — those orgs only have the default roles — so
 * `roles` always contains at least the {@link DefaultRole}s, even while loading or when gated.
 *
 * To list only the non-default (stored custom) roles — e.g. to render them with their full
 * `id` / `color` / `description` — read the raw `data` instead of `roles`.
 *
 * @param args.search - Optional free-text search forwarded to the `getSome` request.
 * @param args.enabled - Force-disable the query regardless of plan gating. Defaults to enabled.
 * @param args.select - Optional transform applied to the merged default + custom roles. When provided,
 *   `roles` is the transformed value; otherwise `roles` is the merged {@link RoleOrDefault} array.
 *
 * @returns The underlying infinite query result, plus:
 *   - `data`: the raw custom {@link RoleRecord} array (non-default roles only; `undefined` while loading)
 *   - `roles`: the merged default + custom roles, optionally transformed by `select`
 *   - `defaultRoles`: the built-in Admin / Non-Admin roles ({@link DEFAULT_ROLES})
 *   - `hasRoleBasedAccess`: whether the org's plan includes role-based access permissions
 *
 * @example
 * ```tsx
 * // All roles (default + custom) — the source of truth:
 * const { roles } = useRolesQuery();
 * const roleNames = roles.map((r) => r.role);
 *
 * // Only the stored custom roles, with their full record fields:
 * const { data: customRoles } = useRolesQuery();
 *
 * // With a custom select to derive the shape the component needs:
 * const { roles: roleNames } = useRolesQuery({ select: (roles) => roles.map((r) => r.role) });
 * ```
 */
export function useRolesQuery(args?: UseRolesQueryArgs): UseRolesQueryResult & { roles: RoleOrDefault[] };
export function useRolesQuery<TSelected>(
  args: UseRolesQueryArgs<TSelected> & { select: (roles: RoleOrDefault[]) => TSelected }
): UseRolesQueryResult & { roles: TSelected };
export function useRolesQuery<TSelected = RoleOrDefault[]>({
  search,
  enabled = true,
  select,
}: UseRolesQueryArgs<TSelected> = {}) {
  const { plan } = useOrgPlan();
  const hasRoleBasedAccess = planSupportsRoleBasedAccess(plan);

  const trimmedSearch = search?.trim();
  const query = useTellescopeInfiniteQuery({
    model: "role_based_access_permissions",
    method: "getSome",
    request: { limit: 100, search: trimmedSearch ? { query: trimmedSearch } : undefined },
    select: (data) => data.pages.flat(),
    enabled: enabled && hasRoleBasedAccess,
  });

  const roles = useMemo(
    () => (select ?? identityRolesSelect)([...DEFAULT_ROLES, ...(query.data ?? [])]),
    [query.data, select]
  );

  return { ...query, roles, defaultRoles: DEFAULT_ROLES, hasRoleBasedAccess };
}
